/**
 * Die Gliederung des Gewerks Heiz-/Kühllast — an EINER Stelle.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * DAS MUSTER (N1, verbindlich für N2, N3 und N4)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * So legt man einen Bereich an, ohne nachzufragen:
 *
 * 1. `seiten/<bereich>/abschnitte.ts` anlegen (wie hier) — Abschnitt und
 *    Eintrag, mit `pfad` (die ECHTE, in App.tsx registrierte Route unter
 *    `/projekte/:id/`), `name` (die Beschriftung) und optional `hinweis`
 *    (Tooltip). Vorbild `seiten/elektro/abschnitte.ts`.
 * 2. In `navigation/struktur.ts` eine Funktion `<bereich>Eintraege()` nach
 *    dem Muster von `elektroEintraege()` ergänzen: `flatMap` über die
 *    Abschnitte, `art` wird der Abschnittsname, `eltern` wird
 *    `fachbereichSchluessel('<roleKey>')`. In `alleEintraege()` einhängen
 *    (`const heizlastkuehllast = projektId ? heizlastkuehllastEintraege() : [];`
 *    und in `roh` aufnehmen).
 * 3. Der Eintrag, dessen `pfad` GENAU dem Pfad des Fachbereichs entspricht
 *    (hier: `heizlast-kuehllast` selbst, Abschnitt "Berechnungen" →
 *    "Heizlast"), gehört trotzdem in die Liste — `alleEintraege()` filtert
 *    ihn automatisch heraus (Kind == Elternpfad), aber er dokumentiert den
 *    vollständigen Satz an Reitern. Vorbild: `elektro`/"Bauteile".
 * 4. `art` ist KEIN fester Wert aus `ART_BESCHRIFTUNG` — jeder
 *    Abschnittsname wird 1:1 zur Zwischenzeile (`artBeschriftung()` fällt
 *    auf den Rohwert zurück). Trotzdem gilt: **`baum()` steigt NICHT
 *    rekursiv ab** — `artenVon()` verarbeitet nur die direkten Kinder eines
 *    Wurzelknotens. Ein Eintrag, dessen `eltern` selbst ein Kind ist (ein
 *    Enkel), landet in KEINER Artgruppe und ist unsichtbar. Jeder Eintrag
 *    hier braucht `eltern: fachbereichSchluessel('<roleKey>')` — niemals
 *    den Schlüssel eines Geschwistereintrags.
 * 5. Wo mehr als etwa ein Dutzend Unterpunkte zusammenkommen, ist eine
 *    ZUSÄTZLICHE `art` der richtige Weg, kein Sammelknoten (Plan Abschnitt
 *    7). Jeder Eintrag trägt außerdem `sichtbarkeit: { art: 'projekt' }`
 *    (oder `'fachbereich'`, wenn er unabhängig vom offenen Projekt gilt,
 *    was bei Unterseiten praktisch nie zutrifft) — wer sie vergisst, baut
 *    einen Eintrag, der ganz verschwindet oder ohne Projekt auftaucht.
 * 6. Existiert für einen geplanten Unterpunkt noch keine Seite (Plan
 *    Abschnitt 6: "wo keine Seite existiert, ist der Unterpunkt Arbeit und
 *    nicht nur Navigation"), gehört er NICHT hierher — Navigation verweist
 *    nur auf real Vorhandenes.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WARUM FÜNF UNTERPUNKTE UND NICHT DIE NEUN AUS DEM PLANENTWURF
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Plan Abschnitt 3.1 schlug u. a. "Heizung & Kälte", "Klimadaten",
 * "Nutzungsprofile", "Raumwerte" und "Befunde" als eigene Unterpunkte vor.
 * Gemessen an der bestehenden Seite (vormals EINE Route mit fünf internen
 * Reitern) und den tatsächlich vorhandenen Seiten:
 *
 *   - "Heizung & Kälte" ist NICHT dieser Fachbereich. `heizung_kaelte` und
 *     `heizlast_kuehllast` sind zwei getrennte `disciplines`-Zeilen mit
 *     eigenem Pfad (`heizung-kaelte` bzw. `heizlast-kuehllast`) und eigenem
 *     Wurzelknoten in `struktur.ts`. `heizung_kaelte` hat bereits drei
 *     Unterseiten (Rohrnetz, Datenpflege, Geräteliste) - sie hier ein
 *     zweites Mal als Kind einzuhängen, würde denselben Pfad unter zwei
 *     Eltern zeigen und die Ein-Eintrag-pro-Route-Regel verletzen
 *     (`struktur.test.ts`, „Vollstaendigkeit: Baum gegen App.tsx").
 *   - "Klimadaten", "Nutzungsprofile", "Raumwerte" und "Befunde" als EIGENE,
 *     separat navigierbare Seiten existieren nicht - der Standort- und
 *     Klimareiter deckt Klimadaten/Randbedingungen/Referenzwerte bereits ab
 *     (siehe `HeizlastKlimaSeite`), und für Nutzungsprofile/Raumwerte/
 *     Befunde gibt es keine Route zum Verweisen. Sie sind Arbeit, keine
 *     Navigation (Plan Abschnitt 6).
 *
 * Die tatsächlichen fünf Reiter der Seite VOR N1 waren: Wärmebedarf,
 * Kühllast, Jahresgang, Standort und Klima, Allgemein - alle fünf sind
 * unten aufgeführt, vier davon als eigene Route, einer (s. u.) bewusst
 * eingebettet geblieben.
 */

export interface HeizlastKuehllastEintrag {
  /** Der Pfad UNTER /projekte/:id/ — genau wie in App.tsx. */
  pfad: string;
  name: string;
  /** Wozu die Seite da ist. Erscheint als Titel am Baumeintrag. */
  hinweis: string;
}

export interface HeizlastKuehllastAbschnitt {
  name: string;
  eintraege: HeizlastKuehllastEintrag[];
}

export const HEIZLASTKUEHLLAST_ABSCHNITTE: HeizlastKuehllastAbschnitt[] = [
  {
    name: 'Berechnungen',
    eintraege: [
      {
        pfad: 'heizlast-kuehllast',
        name: 'Heizlast',
        hinweis: 'Der Berechnungslauf nach DIN EN 12831-1: Außentemperatur '
          + 'wählen, rechnen, Ergebnis samt Jahressimulation und Raumtabelle',
      },
      {
        pfad: 'heizlast-kuehllast/kuehllast',
        name: 'Kühllast',
        hinweis: 'Der Auslegungstag nach VDI 2078, stundenweise: solarer '
          + 'Eintrag, innere Lasten, Lüftung mit Rückgewinnung',
      },
      {
        // Der Jahresgang der HEIZLAST (Heizjahresgang.tsx) bleibt in der
        // Route "Heizlast" eingebettet - siehe die Begründung im
        // Kopfkommentar von HeizlastKuehllastRahmen. Dieser Eintrag ist
        // ausdrücklich nur die Jahressimulation der KÜHLLAST.
        pfad: 'heizlast-kuehllast/jahresgang',
        name: 'Jahresgang',
        hinweis: 'Die Jahressimulation der Kühllast über 8.760 Stunden '
          + 'DWD-Messreihe München-Flughafen — mit Vollbenutzungsstunden '
          + 'und freier Kühlung',
      },
    ],
  },
  {
    name: 'Daten',
    eintraege: [
      {
        pfad: 'heizlast-kuehllast/klima',
        name: 'Standort und Klima',
        hinweis: 'Die Eingangsgrößen (Klimadaten, übernommene '
          + 'Randbedingungen) und die externen Referenzwerte, die das '
          + 'Ergebnis NUR prüfen, nicht speisen',
      },
      {
        pfad: 'heizlast-kuehllast/raumtemperaturen',
        name: 'Raumtemperaturen',
        hinweis: 'Die Vorlage des Fachplaners vom 26.06.2026 mit ihren '
          + 'Bändern, die Zuordnungsregeln und je Raum die Herkunft seiner '
          + 'Solltemperatur — fachplaner, norm oder fehlt',
      },
    ],
  },
  {
    name: 'Prüfung',
    eintraege: [
      {
        // TL2: eine Liste für Seite, Annahmenblatt und Vorbehalt.
        pfad: 'heizlast-kuehllast/annahmen',
        name: 'Annahmen und Vorgaben',
        hinweis: 'Welche Vorgaben Heiz- und Kühllast lesen und ob sie bestätigt '
          + 'sind - bestätigen mit Begründung und Quelle',
      },
      {
        // TL10: nur Übernahme und Abgleich, der GEG-Nachweis ist nicht Leistungsumfang (E5).
        pfad: 'heizlast-kuehllast/geg-abgleich',
        name: 'GEG-Abgleich',
        hinweis: 'Je Bauteiltyp des Wärmeschutznachweises: Nachweiswerte neben Fläche und U-Wert der '
          + 'Rechnung aus dem Modell - mit Befunden, ohne etwas zu entscheiden',
      },
      {
        pfad: 'heizlast-kuehllast/allgemein',
        name: 'Allgemein',
        hinweis: 'Der Stand des Moduls: was steht, was noch fehlt — keine '
          + 'eigene Fachfrage, aber der Ort für Modul-Befunde, solange es '
          + 'keine eigene Befundeliste gibt',
      },
    ],
  },
];
